import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'architect',
    emoji: '🏛️',
    title: '건축가',
    category: '건축·설계',
    color: 'from-sky-400 to-blue-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    badge: 'bg-sky-100 text-sky-700',
    story: `건축가 지수씨는 새 건물의 벽면 타일 배치를 설계하고 있습니다.
직사각형 벽면의 가로 길이는 세로 길이보다 3m 더 깁니다.
벽면 둘레가 26m일 때 각 면의 길이를 구해야 합니다.
"세로를 x로 놓으면 가로는 x+3, 둘레 공식으로 방정식을 세우면 되지!"
지수씨는 기본 도형 개념으로 문제를 풀었습니다.`,
    situation: '직사각형 둘레 = 2×(가로+세로). 가로 = 세로 + 3, 둘레 = 26m 조건.',
    question: '벽면의 가로와 세로 길이는 각각 몇 m일까요?',
    xLabel: '세로 길이 (m)',
    yLabel: '둘레 (m)',
    slope: 4,
    intercept: 6,
    slopeDesc: '세로 1m 증가 → 둘레 4m 증가 (2×가로+2×세로)',
    interceptDesc: '고정 부분: 2×3 = 6 (가로와 세로의 차이 반영)',
    equation: '2(x + x + 3) = 26',
    answer: '2(2x+3) = 26 → 4x+6 = 26 → 4x = 20\nx = 5m (세로), 가로 = 8m',
    whyGeometry: '직사각형 둘레 공식 2(가로+세로)에 조건 대입 → 방정식 완성! 건축 설계의 핵심.',
    insight: '건축가·인테리어 디자이너·토목기사는 공간 설계 시 항상 도형의 둘레·넓이 공식을 활용합니다.',
    realAnswer: 5,
    unit: 'm',
    tableData: [
      { x: 3, y: 18 }, { x: 4, y: 22 }, { x: 5, y: 26 },
      { x: 6, y: 30 }, { x: 7, y: 34 }, { x: 8, y: 38 },
    ],
    quiz: [
      { q: '세로가 4m일 때 둘레는 몇 m인가요?', a: '22m', hint: '가로=4+3=7, 둘레=2×(7+4)=2×11=22' },
      { q: '둘레가 30m일 때 세로 길이는?', a: '6m', hint: '2(x+x+3)=30 → 4x+6=30 → 4x=24 → x=6' },
    ],
    jobs: ['건축가', '인테리어 디자이너', '토목기사', '도시계획가'],
  },
  {
    id: 'game',
    emoji: '🎮',
    title: '게임 레벨 디자이너',
    category: 'IT·게임',
    color: 'from-indigo-400 to-violet-500',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    badge: 'bg-indigo-100 text-indigo-700',
    story: `게임 레벨 디자이너 민준씨는 격자(grid) 맵을 설계합니다.
n×n 격자 맵에서 두 점 A(1,2)와 B(4,6)의 거리를 계산해야 합니다.
"피타고라스 정리를 사용하면 직각삼각형으로 분해할 수 있어!"
두 점 사이의 거리를 구하면 플레이어 이동 경로를 정밀하게 설계할 수 있습니다.`,
    situation: '두 점 A(1,2), B(4,6) 사이 거리. 수평 거리=3, 수직 거리=4인 직각삼각형.',
    question: 'A와 B 사이의 최단 거리(직선 거리)는 얼마인가요?',
    xLabel: '수평 거리 (칸)',
    yLabel: '직선 거리 (칸)',
    slope: 0,
    intercept: 4,
    slopeDesc: '수직 거리 4 고정 → 피타고라스로 계산',
    interceptDesc: '수직 거리 4칸 기준값',
    equation: 'd = √(3² + 4²) = √25 = 5',
    answer: '수평 거리=3, 수직 거리=4\nd = √(9+16) = √25 = 5칸',
    whyGeometry: '좌표 위 두 점 → 직각삼각형 → 피타고라스! 게임·지도·내비게이션의 거리 계산 원리.',
    insight: '게임 개발자·내비게이션 엔지니어·GIS 전문가는 좌표와 직각삼각형으로 최단 거리를 계산합니다.',
    realAnswer: 5,
    unit: '칸',
    tableData: [
      { x: 0, y: 4 }, { x: 3, y: 5 }, { x: 4, y: 5.66 },
      { x: 5, y: 6.4 }, { x: 6, y: 7.21 }, { x: 8, y: 8.94 },
    ],
    quiz: [
      { q: '수평 거리 0, 수직 거리 4일 때 두 점 거리는?', a: '4칸', hint: '√(0²+4²) = √16 = 4' },
      { q: '수평 6, 수직 8일 때 두 점 거리는?', a: '10칸', hint: '√(36+64) = √100 = 10 (6-8-10 피타고라스 수!)' },
    ],
    jobs: ['게임 레벨 디자이너', '내비게이션 개발자', 'GIS 전문가', 'VR/AR 개발자'],
  },
  {
    id: 'surveyor',
    emoji: '📏',
    title: '측량기사',
    category: '공학·측량',
    color: 'from-emerald-400 to-teal-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `측량기사 수진씨는 두 직선 도로가 만나는 교차로 각도를 측정합니다.
도로 A와 도로 B가 교차할 때 생기는 4개의 각도 중
한 각이 65°로 측정됐습니다.
"맞꼭지각과 동위각 성질을 이용하면 나머지 각도도 모두 구할 수 있어!"
수진씨는 평행선 각도 성질을 활용합니다.`,
    situation: '두 직선이 교차 → 맞꼭지각은 같고, 이웃각의 합 = 180°. 한 각이 65°일 때 나머지 각 계산.',
    question: '65°의 맞꼭지각과 이웃각은 각각 몇 도인가요?',
    xLabel: '한 각도 (°)',
    yLabel: '이웃각 (°)',
    slope: -1,
    intercept: 180,
    slopeDesc: '각도 1° 증가 → 이웃각 1° 감소 (합=180°)',
    interceptDesc: '이웃각의 합 180°',
    equation: 'x + 이웃각 = 180°',
    answer: '맞꼭지각 = 65°\n이웃각 = 180° − 65° = 115°',
    whyGeometry: '맞꼭지각 = 같다, 이웃각의 합 = 180°. 두 직선 교차의 핵심 성질!',
    insight: '측량기사·도로설계사·건축가는 교차 각도와 평행선 성질을 매일 활용해 정확한 설계를 합니다.',
    realAnswer: 115,
    unit: '°',
    tableData: [
      { x: 30, y: 150 }, { x: 45, y: 135 }, { x: 60, y: 120 },
      { x: 65, y: 115 }, { x: 90, y: 90 }, { x: 120, y: 60 },
    ],
    quiz: [
      { q: '한 각이 72°일 때 이웃각은?', a: '108°', hint: '이웃각 = 180° − 72° = 108°' },
      { q: '두 직선이 직각으로 교차하면 4개 각도는 모두 몇 도인가요?', a: '모두 90°', hint: '한 각=90° → 이웃각=90°, 맞꼭지각=90°' },
    ],
    jobs: ['측량기사', '도로 설계사', '건축구조기술사', '토목공학자'],
  },
  {
    id: 'fashion',
    emoji: '👗',
    title: '패션 디자이너',
    category: '예술·디자인',
    color: 'from-rose-400 to-pink-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    badge: 'bg-rose-100 text-rose-700',
    story: `패션 디자이너 예은씨는 원형 스커트 패턴을 설계합니다.
스커트 밑단(원의 둘레)이 188.4cm가 되어야 합니다.
"원의 둘레 = 2πr 공식으로 반지름을 역산하면 되지!"
원단의 낭비 없이 정확한 반지름을 계산해야 합니다. (π ≈ 3.14)`,
    situation: '원의 둘레 C = 2πr = 188.4cm. r(반지름)을 구해 원형 패턴 재단.',
    question: '스커트 밑단 반지름은 몇 cm인가요?',
    xLabel: '반지름 (cm)',
    yLabel: '둘레 (cm)',
    slope: 6.28,
    intercept: 0,
    slopeDesc: '반지름 1cm 증가 → 둘레 약 6.28cm 증가 (2π)',
    interceptDesc: '반지름 0이면 둘레 0 (원점 통과)',
    equation: '2 × 3.14 × r = 188.4',
    answer: '6.28r = 188.4\nr = 188.4 ÷ 6.28 = 30cm',
    whyGeometry: '원 둘레 공식 C=2πr → r 역산! 모든 원형 설계의 기본. π를 이해하면 모든 원을 설계할 수 있습니다.',
    insight: '패션 디자이너·목수·기계공학자는 원의 둘레·넓이 공식으로 정확한 치수를 계산합니다.',
    realAnswer: 30,
    unit: 'cm',
    tableData: [
      { x: 10, y: 62.8 }, { x: 20, y: 125.6 }, { x: 25, y: 157 },
      { x: 30, y: 188.4 }, { x: 35, y: 219.8 }, { x: 40, y: 251.2 },
    ],
    quiz: [
      { q: '반지름 20cm인 원의 둘레는?', a: '125.6cm', hint: '2×3.14×20 = 6.28×20 = 125.6' },
      { q: '둘레가 314cm일 때 반지름은?', a: '50cm', hint: '6.28r=314 → r=314÷6.28=50' },
    ],
    jobs: ['패션 디자이너', '목수·가구 디자이너', '기계공학자', '그래픽 디자이너'],
  },
  {
    id: 'pilot',
    emoji: '✈️',
    title: '항공기 조종사',
    category: '항공·교통',
    color: 'from-cyan-400 to-sky-500',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    badge: 'bg-cyan-100 text-cyan-700',
    story: `항공기 조종사 준혁씨는 두 활주로가 이루는 각도를 파악해야 합니다.
활주로 A와 평행한 유도로 B가 있고, 연결 도로 C가 두 선을 가로질러 지나갑니다.
연결도로 C와 유도로 B가 이루는 각도가 55°일 때,
동위각 성질로 활주로 A와 연결도로 C의 각도를 구해야 합니다.`,
    situation: '평행한 두 선(A, B)을 가로지르는 선(C). 동위각=같다, 엇각=같다 성질 활용.',
    question: '평행선 A와 연결도로 C가 이루는 각도(동위각)는 몇 도인가요?',
    xLabel: '유도로 각도 (°)',
    yLabel: '동위각 (°)',
    slope: 1,
    intercept: 0,
    slopeDesc: '동위각은 항상 같음 (평행선 성질)',
    interceptDesc: '평행선에서 동위각 = 원래 각도',
    equation: '동위각 = 55° (평행선 성질)',
    answer: '평행선에서 동위각은 서로 같습니다.\n활주로 A와 연결도로의 각도 = 55°',
    whyGeometry: '평행선 + 가로지르는 선 → 동위각·엇각·동측내각! 항공·도로·철도 설계의 핵심 기하학.',
    insight: '항공관제사·철도설계사·도로공학자는 평행선 각도 성질로 정확한 방향각·경로각을 계산합니다.',
    realAnswer: 55,
    unit: '°',
    tableData: [
      { x: 30, y: 30 }, { x: 45, y: 45 }, { x: 55, y: 55 },
      { x: 60, y: 60 }, { x: 75, y: 75 }, { x: 90, y: 90 },
    ],
    quiz: [
      { q: '유도로 각도가 70°일 때 동위각은?', a: '70°', hint: '동위각은 평행선에서 항상 같습니다!' },
      { q: '엇각이 55°이면 그 반대쪽 엇각은?', a: '55°', hint: '엇각도 평행선에서 항상 같습니다!' },
    ],
    jobs: ['항공관제사', '철도설계사', '도로공학자', '항법사'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '🏛️',
    title: '건축가·인테리어 디자이너',
    example: '방 둘레·넓이로 자재 수량 계산',
    formula: '둘레 = 2(가로+세로),  넓이 = 가로×세로',
    salary: '연 4,000만~1억원',
    color: 'from-sky-500 to-blue-500',
  },
  {
    icon: '🎮',
    title: '게임·VR 개발자',
    example: '좌표 두 점 사이 최단 거리 계산',
    formula: 'd = √(Δx² + Δy²)  (피타고라스)',
    salary: '연 4,500만~1억원',
    color: 'from-indigo-500 to-violet-500',
  },
  {
    icon: '📏',
    title: '측량기사·토목공학자',
    example: '교차로 각도·평행선 각도 계산',
    formula: '이웃각 = 180° − θ,  맞꼭지각 = θ',
    salary: '연 4,000만~8,000만원',
    color: 'from-emerald-500 to-teal-500',
  },
  {
    icon: '👗',
    title: '패션·제품 디자이너',
    example: '원형 패턴 반지름·넓이 계산',
    formula: 'C = 2πr,  S = πr²',
    salary: '연 3,500만~8,000만원',
    color: 'from-rose-500 to-pink-500',
  },
  {
    icon: '✈️',
    title: '항공관제사·도로설계사',
    example: '평행선 동위각·엇각으로 경로각 계산',
    formula: '동위각 = θ,  엇각 = θ,  동측내각 = 180°−θ',
    salary: '연 5,000만~1.2억원',
    color: 'from-cyan-500 to-sky-500',
  },
  {
    icon: '🔭',
    title: '천문학자·위성공학자',
    example: '천체 간 각도와 거리 계산',
    formula: '직각삼각형 + 삼각함수로 거리 역산',
    salary: '연 5,000만~1억원',
    color: 'from-purple-500 to-indigo-500',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '📐',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '두 직선이 교차할 때 한 각이 40°입니다. 나머지 세 각도를 모두 구하세요.',
    hint: '맞꼭지각=40°, 이웃각=180°−40°=140°, 반대편 이웃각도 140°',
    answer: '40°, 140°, 40°, 140°',
    explanation: '맞꼭지각은 서로 같고, 이웃각의 합은 180°. 40°와 140°가 번갈아 2쌍!',
  },
  {
    id: 'c2',
    emoji: '🏛️',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '직사각형 방의 둘레가 32m이고, 가로가 세로의 3배입니다. 가로와 세로의 길이를 구하세요.',
    hint: '세로=x, 가로=3x → 2(3x+x)=32 → 8x=32 → x=4',
    answer: '세로 = 4m, 가로 = 12m',
    explanation: '2(3x+x)=32 → 2×4x=32 → 8x=32 → x=4. 세로 4m, 가로 12m 확인: 2(12+4)=32 ✓',
  },
  {
    id: 'c3',
    emoji: '📏',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '평행한 두 직선 l, m을 가로지르는 직선이 있습니다. l과의 각도가 (2x+10)°, m과의 동위각이 (3x−20)°일 때 x의 값을 구하세요.',
    hint: '동위각은 같으므로 2x+10 = 3x−20 → x = 30',
    answer: 'x = 30 (각도 = 70°)',
    explanation: '동위각은 서로 같다 → 2x+10 = 3x−20 → −x = −30 → x = 30. 각도 = 2×30+10 = 70° 확인!',
  },
  {
    id: 'c4',
    emoji: '✈️',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '공항에서 비행기가 정북 방향(0°)에서 출발해 동쪽(90°)으로 300km, 남쪽으로 400km 이동했습니다. 출발점에서 현재 위치까지의 직선 거리는?',
    hint: '수평 300km, 수직 400km → 피타고라스: √(300²+400²) = √250000 = 500',
    answer: '500 km',
    explanation: '직각삼각형 → √(300²+400²) = √(90000+160000) = √250000 = 500km. 300-400-500은 피타고라스 수!',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '건축·설계': 'bg-sky-100 text-sky-700',
  'IT·게임': 'bg-indigo-100 text-indigo-700',
  '공학·측량': 'bg-emerald-100 text-emerald-700',
  '예술·디자인': 'bg-rose-100 text-rose-700',
  '항공·교통': 'bg-cyan-100 text-cyan-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
type CalcMode = 'forward' | 'inverse'

interface CalcResult {
  mode: 'forward' | 'inverse'
  inputVal: number
  outputVal: number
  steps: string[]
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('forward')
  const [inputVal, setInputVal] = useState('')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => { setInputVal(''); setResult(null); setUserAnswer(''); setChecked(null) }
  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const v = parseFloat(inputVal)
    if (isNaN(v)) return

    if (mode === 'forward') {
      const raw = scenario.slope * v + scenario.intercept
      const y = Math.round(raw * 100) / 100
      setResult({
        mode: 'forward',
        inputVal: v,
        outputVal: y,
        steps: [
          `공식: ${scenario.equation}`,
          `x = ${v} 대입`,
          `y = ${scenario.slope} × ${v} + (${scenario.intercept})`,
          `y = ${scenario.slope * v} + (${scenario.intercept})`,
          `y = ${y.toLocaleString()} ${scenario.unit}`,
        ],
      })
    } else {
      if (scenario.slope === 0) return
      const raw = (v - scenario.intercept) / scenario.slope
      const x = Math.round(raw * 100) / 100
      setResult({
        mode: 'inverse',
        inputVal: v,
        outputVal: x,
        steps: [
          `방정식: ${scenario.equation}`,
          `목표값 = ${v.toLocaleString()}으로 설정`,
          `${scenario.slope}x = ${v} − (${scenario.intercept}) = ${v - scenario.intercept}`,
          `x = ${v - scenario.intercept} ÷ ${scenario.slope}`,
          `x = ${x.toLocaleString()} ${scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const tolerance = Math.abs(result.outputVal) * 0.02 + 1
    setChecked(Math.abs(ua - result.outputVal) <= tolerance)
  }

  const xLabelShort = scenario.xLabel.split(' ')[0]
  const yLabelShort = scenario.yLabel.split(' ')[0]

  return (
    <div className="card border-2 border-sky-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-sky-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('forward')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'forward' ? 'bg-sky-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            {xLabelShort} → {yLabelShort}
          </button>
          <button
            onClick={() => handleModeChange('inverse')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'inverse' ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            역산 (풀기)
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">공식</span>
        <span className="font-mono font-bold text-sky-700 text-lg">{scenario.equation}</span>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">
            {mode === 'forward'
              ? `🔢 ${scenario.xLabel} 를 입력하세요`
              : `🎯 목표 ${scenario.yLabel} 를 입력하세요`}
          </label>
          <input
            type="number"
            value={inputVal}
            onChange={e => { setInputVal(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="숫자를 입력하고 계산!"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={!inputVal}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
              mode === 'forward'
                ? 'bg-sky-600 hover:bg-sky-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
                : 'bg-purple-600 hover:bg-purple-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
            }`}
          >
            계산!
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3 flex items-center gap-1">
              📝 풀이 과정 (단계별)
            </p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1 ? 'bg-emerald-500 text-white' : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1 ? 'font-black text-emerald-700 text-base' : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'forward' ? 'bg-sky-50 border-sky-300' : 'bg-purple-50 border-purple-300'
          }`}>
            <p className="text-xs font-semibold text-slate-500 mb-1">
              {mode === 'forward'
                ? `${scenario.xLabel} = ${result.inputVal} 일 때`
                : `목표 ${scenario.yLabel} = ${result.inputVal.toLocaleString()} ${scenario.unit} 일 때`}
            </p>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${mode === 'forward' ? 'text-sky-700' : 'text-purple-700'}`}>
                {result.outputVal.toLocaleString()}
              </span>
              <span className="text-base font-semibold text-slate-500">
                {mode === 'forward' ? scenario.unit : scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}
              </span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder={`내 답: __ ${mode === 'forward' ? scenario.unit : ''}`}
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉</>
                  : <><XCircle size={16} /> 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-emerald-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ch.emoji}</span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
        </div>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-emerald-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-emerald-600 leading-relaxed">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function G1_1RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 기본 도형', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 기본 도형</h1>
            <p className="text-sm text-slate-500">건축가, 게임 개발자, 측량기사, 디자이너, 조종사 — 모두 기본 도형을 활용합니다!</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 기본 도형을 만나다!</p>
          <p className="text-sm opacity-90 mb-3">점·선·각·도형의 성질이 세상 모든 설계와 측량의 기초입니다.</p>
          <div className="grid grid-cols-4 md:grid-cols-5 gap-2">
            {[
              { icon: '🏛️', job: '건축가' },
              { icon: '🎮', job: '게임 개발자' },
              { icon: '📏', job: '측량기사' },
              { icon: '👗', job: '패션 디자이너' },
              { icon: '✈️', job: '항공관제사' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-sky-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-sky-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-sky-600 font-semibold">{scenario.equation}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-2 space-y-4">
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 기본 도형</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.story}</p>
                </div>

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 도형 공식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-2xl font-black text-sky-700 font-mono">{selected.equation}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
                      <p className="text-xs text-orange-500 font-semibold">변화율 (= {selected.slope})</p>
                      <p className="text-xs text-orange-700 mt-0.5">{selected.slopeDesc}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <p className="text-xs text-blue-500 font-semibold">기준값 (= {selected.intercept})</p>
                      <p className="text-xs text-blue-700 mt-0.5">{selected.interceptDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-emerald-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-sky-700">
                    <span className="font-semibold">왜 기본 도형인가?</span> {selected.whyGeometry}
                  </p>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={15} className="text-sky-500" /> 표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '표 접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
                {showTable && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">{selected.xLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-sky-50 text-sky-700 text-left">{selected.yLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-slate-50 text-slate-600 text-left">변화율</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-sky-700">{row.y.toLocaleString()}</td>
                            <td className="border border-slate-200 px-3 py-2 text-xs text-slate-500">
                              {i === 0 ? '—' : (
                                <span className="font-mono text-emerald-600">
                                  {((row.y - selected.tableData[i - 1].y) / (row.x - selected.tableData[i - 1].x)).toFixed(2)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!showTable && (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500 text-sm">
                    <p className="text-2xl mb-1">📋</p>
                    <p>표 펼치기를 눌러 값의 변화를 확인해보세요!</p>
                  </div>
                )}
              </div>

              <ScenarioCalculator scenario={selected} />

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-sky-500" /> 이 상황에서 기본 도형을 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-sky-100 text-sky-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['점·선·면', '각도', '평행선', '도형 공식'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 card bg-gradient-to-br from-sky-50 to-blue-50 border-sky-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 기본 도형 실생활 핵심 공식</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '📐', title: '각도 성질', desc: '맞꼭지각=같다, 이웃각의 합=180°, 동위각·엇각=같다(평행선). 모든 설계의 각도 계산 기본!', color: 'text-sky-600' },
                { icon: '🔷', title: '도형 둘레·넓이', desc: '직사각형: 2(가로+세로), 원: 2πr. 건축·패션·제조의 치수 계산 핵심 공식!', color: 'text-blue-600' },
                { icon: '📏', title: '피타고라스 정리', desc: '직각삼각형: a²+b²=c². 두 점 거리, 건물 높이, 항로 거리 모두 이 공식 하나로!', color: 'text-indigo-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 기본 도형을 사용하는 직업들</h2>
            <p className="text-sm text-sky-100">
              점·선·각·도형의 성질은 설계·측량·항법·게임 개발 등 수많은 직업의 핵심 도구입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-sky-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 기본 도형이 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: '컴퓨터 그래픽·게임 엔진', desc: '3D 공간의 모든 물체는 삼각형의 집합. 기본 도형 없이 게임·영화 특수효과 불가!', color: 'border-yellow-400' },
                { title: '자율주행·로봇 내비게이션', desc: '장애물 감지·경로 계산 모두 좌표와 각도 기반. 피타고라스·각도 성질이 핵심!', color: 'border-blue-400' },
                { title: '건축·도시 설계 CAD', desc: '모든 건물·도로 설계는 도형 공식으로 자동 계산. 기하학이 설계의 언어!', color: 'border-emerald-400' },
                { title: '위성·GPS 측위', desc: '위성 신호로 위치를 계산할 때 삼각형·각도·거리 공식을 활용합니다.', color: 'border-purple-400' },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card border border-sky-200 bg-sky-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-sky-600" /> 기본 도형에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '기본 도형', active: true },
                { step: '중1', topic: '평면도형', active: false },
                { step: '중1', topic: '입체도형', active: false },
                { step: '중2', topic: '삼각형·사각형', active: false },
                { step: '중3', topic: '피타고라스', active: false },
                { step: '고1', topic: '삼각비', active: false },
                { step: '직업', topic: 'CAD·GIS·설계', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-sky-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-sky-500 to-blue-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-sky-100">
              실제 상황에서 기본 도형의 성질을 직접 적용해보세요!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-sky-500" /> 기본 도형 핵심 공식 정리
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '① 각도 성질', desc: '맞꼭지각=같다\n이웃각의 합=180°\n동위각=엇각=같다(평행선)', example: 'θ + 이웃각 = 180°', color: 'bg-sky-50 border-sky-200 text-sky-800' },
                { step: '② 도형 공식', desc: '직사각형 둘레=2(a+b)\n원 둘레=2πr\n원 넓이=πr²', example: '2(x+x+3)=26 → x=5', color: 'bg-blue-50 border-blue-200 text-blue-800' },
                { step: '③ 피타고라스', desc: 'a²+b²=c²\n(직각삼각형 빗변)\n두 점 거리 계산', example: '√(3²+4²)=√25=5', color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2 whitespace-pre-line">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-sky-50 border border-sky-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-sky-700">각도 → 도형 → 피타고라스 → 설계!</p>
              <p className="text-xs text-sky-500 mt-1">기본 도형의 성질을 알면 세상 모든 공간을 수식으로 표현할 수 있습니다!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
